export const tutorial = {
  slug: "tutorial-de-erastro",
  title: "Tutorial de eRastro",
  image: "https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?w=800&q=80",
  excerpt: "Aprende a utilizar nuestra herramienta para sacarle el máximo partido.",
  content: (
    <>
      <h2>Cómo usar eRastro: Guía completa</h2>
      <p>eRastro es tu asistente personal para encontrar ropa de segunda mano. En lugar de perder horas buscando en múltiples plataformas, tú nos dices qué quieres y nosotros lo encontramos por ti.</p>
      <p>En esta guía te explicamos cómo sacar el máximo partido a las dos herramientas principales de eRastro: <strong>las Alertas</strong> y el <strong>Rastreador de Productos</strong>.</p>

      <h2>1. Sistema de Alertas: Busca sin esfuerzo</h2>
      <p>Las alertas son búsquedas automáticas que trabajan por ti las 24 horas del día. Configuras una vez lo que buscas y el sistema te notifica cuando aparecen productos que coinciden con tus criterios.</p>

      <h3>¿Cómo crear una alerta?</h3>
      <p>Crear una alerta es muy sencillo. Dirígete a la sección <strong>"Mis Alertas"</strong> y haz clic en <strong>"Crear Alerta"</strong>. Se abrirá un formulario donde puedes especificar:</p>

      <ul>
        <li><strong>Título de la alerta:</strong> Un nombre identificativo para ti (por ejemplo: "Zapatillas Nike Running")</li>
        <li><strong>Sexo:</strong> Hombre o Mujer (esto influye en las categorías disponibles)</li>
        <li><strong>Categoría:</strong> Define el tipo de producto que buscas (Calzado, Ropa superior, Ropa inferior, etc.)</li>
        <li><strong>Subcategorías:</strong> Refina aún más tu búsqueda con hasta dos niveles de subcategorías</li>
        <li><strong>Talla:</strong> Selecciona la talla exacta que necesitas</li>
        <li><strong>Estado:</strong> Define el estado de conservación (Nuevo, Como nuevo, Muy bueno, Bueno, Usado)</li>
        <li><strong>Marca:</strong> Filtra por marcas específicas (Nike, Adidas, Zara, H&M, etc.)</li>
        <li><strong>Color:</strong> Escoge entre 17 colores disponibles</li>
        <li><strong>Precio máximo:</strong> Establece tu presupuesto límite</li>
        <li><strong>Rebajas (usuarios premium):</strong> Busca solo productos rebajados por una cantidad o porcentaje mínimo</li>
      </ul>

      <img src="/imgs/alertas.png" alt="alertas"/>

      <p>Cuantos más criterios especifiques, más precisos serán los resultados. Sin embargo, también puedes dejar algunos campos vacíos para búsquedas más amplias.</p>
      <p>Recomendamos hacer varias alertas para la misma prenda unas más especificas y otras menos especificas para tener más posibilidades de encontrar lo que buscamos</p>

      <h3>¿Cómo funcionan las alertas?</h3>
      <p>Una vez creada la alerta, el sistema empieza a trabajar automáticamente:</p>

      <ol>
        <li>eRastro escanea continuamente productos en plataformas como Vinted, Wallapop y otras webs de segunda mano</li>
        <li>Cuando aparece un producto que cumple <strong>todos</strong> tus criterios, se añade a tu alerta</li>
        <li>Recibirás un mail indicando los productos nuevos se han encontrado en tus alertas</li>
      </ol>

      <h3>Ver los productos de una alerta</h3>
      <p>Para ver los productos que ha encontrado tu alerta:</p>

      <ol>
        <li>Ve a <strong>"Mis Alertas"</strong></li>
        <li>Las alertas con productos nuevos mostrarán un indicador con el número de resultados</li>
        <li>Haz clic en <strong>"Ver Productos"</strong></li>
        <li>Se abrirá una ventana con todos los productos encontrados</li>
      </ol>

      <img src="/imgs/productos-alertas.png" alt="alertas"/>


      <p>Cada producto muestra información completa:</p>
      <ul>
        <li>Imagen del artículo</li>
        <li>Título y descripción</li>
        <li>Antigüedad en eRastro (días desde que se publicó)</li>
        <li>Talla, estado, marca y color</li>
        <li>Ciudad del vendedor</li>
        <li>Plataforma donde está publicado</li>
        <li>Precio actual</li>
        <li><strong>Para usuarios premium:</strong> indicador de rebajas con porcentaje y cantidad ahorrada</li>
      </ul>

      <p>Además, dentro de la ventana de productos puedes usar el <strong>buscador</strong> para filtrar los resultados por palabras clave.</p>

      <h3>Gestiona tus alertas</h3>
      <p>Desde la sección "Mis Alertas" puedes:</p>
      <ul>
        <li><strong>Buscar entre tus alertas:</strong> Usa el buscador para encontrar rápidamente una alerta específica</li>
        <li><strong>Ver detalles:</strong> Consulta los criterios de búsqueda de cada alerta</li>
        <li><strong>Eliminar alertas:</strong> Borra las alertas que ya no necesites</li>
        <li><strong>Monitorear tu límite:</strong> Un dashboard te muestra cuántas alertas has creado y cuántas te quedan disponibles según tu plan</li>
      </ul>

      <h2>2. Rastreador de Productos</h2>
      <p>El Rastreador de Productos es una herramienta <strong>exclusiva para usuarios premium</strong> que te permite guardar productos específicos y monitorear sus cambios de precio automáticamente.</p>

      <h3>Diferencia entre Alertas y Rastreador</h3>
      <p>Es importante entender la diferencia:</p>
      <ul>
        <li><strong>Alertas:</strong> Buscan productos <em>nuevos</em> que coincidan con tus criterios generales</li>
        <li><strong>Rastreador:</strong> Monitorea productos <em>específicos</em> que ya has encontrado para detectar cambios de precio</li>
      </ul>

      <h3>¿Cómo añadir productos al rastreador?</h3>
      <p>Para añadir productos al rastreador puedes hacerlo desde una alerta.</p>
      <p>Cuando veas los productos de una alerta, cada producto tendrá un botón <strong>"Rastrear producto"</strong>. Haz clic y se añadirá a tu rastreador.</p>


      <h3>¿Qué hace el rastreador?</h3>
      <p>Una vez que añades un producto a tu rastreador, el sistema:</p>

      <ol>
        <li>Guarda el producto con su precio actual</li>
        <li>Escanea regularmente el producto para detectar cambios de precio</li>
        <li>Te notifica automáticamente cuando el precio baja</li>
      </ol>

      <h3>Ver tus productos rastreados</h3>
      <p>Para acceder a tus productos rastreados:</p>

      <ol>
        <li>Ve a la sección <strong>"Mis Productos Rastreados"</strong> desde el menú principal</li>
        <li>Verás todos los productos que estás siguiendo en formato de tarjetas</li>
        <li>Un dashboard te muestra cuántos productos tienes guardados y cuántos te quedan disponibles</li>
      </ol>

      <img src="/imgs/rastreador.png" alt="alertas"/>

      <h3>Gestiona tus productos rastreados</h3>
      <p>Desde "Mis Productos Rastreados" puedes:</p>
      <ul>
        <li><strong>Ver el producto:</strong> Haz clic para ir directamente a la página de venta en la plataforma original</li>
        <li><strong>Dejar de rastrear:</strong> Elimina productos que ya no te interesan</li>
        <li><strong>Monitorear tu límite:</strong> Controla cuántos productos tienes rastreados según tu plan</li>
      </ul>

      <h2>Consejos para aprovechar al máximo eRastro</h2>

      <h3>Para las Alertas:</h3>
      <ul>
        <li><strong>Sé específico pero flexible:</strong> Define bien lo que buscas, pero evita ser demasiado restrictivo o podrías perder buenas oportunidades</li>
        <li><strong>Crea varias variadas:</strong> Crea varias alertas para cada producto que buscas con pequeñas diferencias en las características para tener más posibilidades de exito.</li>
        <li><strong>Revisa regularmente:</strong> Los productos de segunda mano se venden rápido, revisa tus alertas con frecuencia</li>
        <li><strong>Usa el buscador de productos:</strong> Dentro de los resultados de una alerta, el buscador te ayuda a encontrar lo que necesitas</li>
      </ul>

      <h3>Para el Rastreador:</h3>
      <ul>
        <li><strong>Añade productos que te gusten pero sean caros:</strong> El rastreador es ideal para esperar a que bajen de precio</li>
        <li><strong>Actúa rápido cuando hay rebaja:</strong> Los productos rebajados se venden rápido, no lo pienses demasiado</li>
        <li><strong>Limpia productos antiguos:</strong> Si un producto lleva mucho tiempo sin rebajarse, considera dejarlo de rastrear para hacer espacio</li>
      </ul>

      <h2>¿Por qué usar eRastro?</h2>
      <p>Comprar ropa de segunda mano es beneficioso en muchos aspectos:</p>
      <ul>
        <li><strong>Ahorro económico:</strong> Consigue las mismas marcas a precios mucho más bajos</li>
        <li><strong>Sostenibilidad:</strong> Reduces el impacto ambiental de la moda rápida</li>
        <li><strong>Piezas únicas:</strong> Encuentra artículos que ya no están disponibles en tiendas</li>
        <li><strong>Ahorro de tiempo:</strong> eRastro busca por ti en múltiples plataformas simultáneamente</li>
      </ul>

      <p><strong>Con eRastro, ya no tienes que dedicar horas a buscar. Nosotros buscamos por ti, tú solo eliges.</strong></p>

      <h2>¿Necesitas ayuda?</h2>
      <p>Si tienes alguna duda o problema, no dudes en contactarnos a través de la sección de contacto. Estamos aquí para ayudarte a encontrar lo que buscas de la forma más fácil posible.</p>
    </>
  )
};
